import { useMemo } from 'react';
import type {
  ILocationGeofenceItem,
  IPersonGeofenceItem,
  IResolvedGeofenceGeometry,
  IResolvedGeofenceItem,
  IResolvedGeofenceStyle,
  ITrackingItem,
  IGeofencePoint,
} from '~/utils/interface/map';
import { toFiniteNumber } from '~/utils/number';

const DEFAULT_STROKE_OPACITY = 0.85;
const DEFAULT_FILL_OPACITY = 0.1;
const DEFAULT_STROKE_WEIGHT = 2;
const DEFAULT_CIRCLE_COLOR = '#2563EB';
const DEFAULT_POLYGON_COLOR = '#30fa7a';

const isValidPoint = (point: Partial<IGeofencePoint> | null | undefined): point is IGeofencePoint => {
  if (!point) return false;
  return Number.isFinite(point.lat) && Number.isFinite(point.lng);
};

const normalizePoints = (points: unknown): IGeofencePoint[] => {
  if (!Array.isArray(points)) return [];

  return points
    .map((point) => {
      if (!point || typeof point !== 'object') return null;
      const candidate = point as Partial<IGeofencePoint>;
      const lat = toFiniteNumber(candidate.lat);
      const lng = toFiniteNumber(candidate.lng);

      if (lat === null || lng === null) return null;
      return { lat, lng };
    })
    .filter(isValidPoint);
};

const normalizeGeofenceIds = (geofenceIds: IPersonGeofenceItem['geofence_ids']): number[] => {
  if (!Array.isArray(geofenceIds)) return [];

  const uniqueIds = new Set<number>();
  for (const geofenceId of geofenceIds) {
    const normalizedId = toFiniteNumber(geofenceId);
    if (normalizedId !== null) {
      uniqueIds.add(normalizedId);
    }
  }

  return Array.from(uniqueIds);
};

const buildGeofenceGeometry = (location: ILocationGeofenceItem): IResolvedGeofenceGeometry | null => {
  const points = normalizePoints(location.points);
  const radius = toFiniteNumber(location.radius);

  if (location.type === 'circle') {
    const center = points[0];

    if (!center || radius === null || radius <= 0) return null;

    return {
      shape: 'circle',
      center,
      radius,
    };
  }

  if (location.type === 'polygon') {
    if (points.length < 3) return null;
    return { shape: 'polygon', points };
  }

  if (location.type === 'square') {
    if (points.length >= 3) {
      return { shape: 'polygon', points };
    }
    if (points.length === 1) {
      return { shape: 'point', center: points[0] };
    }
    return null;
  }

  return null;
};

const resolveBeColor = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'null') return null;
  return trimmed;
};

const buildGeofenceStyle = (location: ILocationGeofenceItem): IResolvedGeofenceStyle => {
  const locationType = typeof location.type === 'string' ? location.type.toLowerCase() : '';
  const locationTypeId = toFiniteNumber(location.location_type);
  const defaultColor =
    locationType === 'circle' || locationTypeId === 0 ? DEFAULT_CIRCLE_COLOR : DEFAULT_POLYGON_COLOR;

  const strokeFromBe = resolveBeColor(location.color);
  const fillFromBe = resolveBeColor(location.fill_color) ?? resolveBeColor(location.fillColor);

  return {
    color: strokeFromBe ?? defaultColor,
    fillColor: fillFromBe ?? strokeFromBe ?? defaultColor,
    fillOpacity: toFiniteNumber(location.fill_opacity) ?? DEFAULT_FILL_OPACITY,
    opacity: toFiniteNumber(location.opacity) ?? DEFAULT_STROKE_OPACITY,
    weight: toFiniteNumber(location.weight) ?? DEFAULT_STROKE_WEIGHT,
  };
};

const getPersonName = (person: IPersonGeofenceItem, tracking: ITrackingItem): string => {
  return person.full_name || person.name || tracking.full_name || tracking.device_name || '--';
};

const getDeviceName = (person: IPersonGeofenceItem, tracking: ITrackingItem): string => {
  return tracking.device_name || person.devname || '--';
};

export const buildGeofenceOverlays = ({
  trackingList,
  personList,
  locationList,
  assignedLocationsByPersonId = {},
}: {
  trackingList: ITrackingItem[];
  personList: IPersonGeofenceItem[];
  locationList: ILocationGeofenceItem[];
  assignedLocationsByPersonId?: Record<number, ILocationGeofenceItem[]>;
}): IResolvedGeofenceItem[] => {
  if (!trackingList.length || !personList.length) return [];

  const personById = new Map<number, IPersonGeofenceItem>();
  for (const person of personList) {
    const personId = toFiniteNumber(person.id);
    if (personId !== null) personById.set(personId, person);
  }

  const locationById = new Map<number, ILocationGeofenceItem>();
  for (const location of locationList) {
    const locationId = toFiniteNumber(location.id);
    if (locationId !== null) locationById.set(locationId, location);
  }
  for (const assignedList of Object.values(assignedLocationsByPersonId)) {
    for (const location of assignedList) {
      const locationId = toFiniteNumber(location.id);
      if (locationId !== null) locationById.set(locationId, location);
    }
  }

  if (!locationById.size) return [];

  const overlays: IResolvedGeofenceItem[] = [];

  for (const tracking of trackingList) {
    const trackingPersonId = toFiniteNumber(tracking.person_id);
    if (trackingPersonId === null) continue;

    const person = personById.get(trackingPersonId);
    if (!person) continue;

    const geofenceIds = normalizeGeofenceIds(person.geofence_ids);
    const assignedLocations = assignedLocationsByPersonId[trackingPersonId] ?? [];
    const locationIdsToRender = new Set<number>(geofenceIds);

    for (const assignedLocation of assignedLocations) {
      const assignedId = toFiniteNumber(assignedLocation.id);
      if (assignedId !== null) {
        locationIdsToRender.add(assignedId);
      }
    }

    if (!locationIdsToRender.size) continue;

    for (const geofenceId of Array.from(locationIdsToRender)) {
      const location =
        locationById.get(geofenceId) ?? assignedLocations.find((item) => toFiniteNumber(item.id) === geofenceId);
      if (!location) continue;

      const geometry = buildGeofenceGeometry(location);
      if (!geometry) continue;

      const style = buildGeofenceStyle(location);

      overlays.push({
        id: `${trackingPersonId}-${geofenceId}`,
        geofenceId,
        geofenceName: location.name || `Geofence ${geofenceId}`,
        personId: trackingPersonId,
        personName: getPersonName(person, tracking),
        deviceName: getDeviceName(person, tracking),
        deviceImei: tracking.device_imei || person.imei || '--',
        status: null,
        statusColor: style.color,
        style,
        geometry,
        tracking,
        person,
        location,
      });
    }
  }

  return overlays;
};

export const useGeofenceOverlays = ({
  trackingList,
  personList,
  locationList,
  assignedLocationsByPersonId,
}: {
  trackingList: ITrackingItem[];
  personList: IPersonGeofenceItem[];
  locationList: ILocationGeofenceItem[];
  assignedLocationsByPersonId?: Record<number, ILocationGeofenceItem[]>;
}): IResolvedGeofenceItem[] => {
  return useMemo(
    () => buildGeofenceOverlays({ trackingList, personList, locationList, assignedLocationsByPersonId }),
    [assignedLocationsByPersonId, locationList, personList, trackingList]
  );
};

export default useGeofenceOverlays;
